export function format(first?: string, middle?: string, last?: string): string {
  return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}

export interface CounterFormatterOptions {
  currentLength: number;
  maxLength?: number;
}

export type CounterFormatter = (options: CounterFormatterOptions) => string;

export function defaultCounterFormatter({ currentLength, maxLength }: CounterFormatterOptions): string {
  return maxLength !== undefined ? `${currentLength} / ${maxLength}` : `${currentLength}`;
}

export function getCounterText(
  value: string | number | null | undefined,
  counter?: boolean | number | null,
  counterFormatter: CounterFormatter = defaultCounterFormatter
): string | null {
  if (counter === false || counter === undefined || counter === null) {
    return null;
  }

  const str = value === null || value === undefined ? '' : String(value);
  const currentLength = str.length;
  const maxLength = typeof counter === 'number' ? counter : undefined;

  return counterFormatter({ currentLength, maxLength });
}

export interface DebouncedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  /** Cancels a pending invocation, if any. */
  cancel: () => void;
  /** Immediately invokes the pending invocation, if any. */
  flush: () => void;
}

export function debounce<T extends (...args: any[]) => any>(
  callback: T,
  wait = 0,
  immediate = false
): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: Parameters<T> | undefined;

  const debounced = function (...args: Parameters<T>): void {
    lastArgs = args;
    const callNow = immediate && timeout === undefined;

    const later = () => {
      timeout = undefined;
      lastArgs = undefined;
      if (!immediate) {
        callback(...args);
      }
    };

    if (timeout !== undefined) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) {
      callback(...args);
      lastArgs = undefined;
    }
  } as DebouncedFunction<T>;

  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
      timeout = undefined;
      lastArgs = undefined;
    }
  };

  debounced.flush = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
      timeout = undefined;
      if (lastArgs !== undefined) {
        callback(...lastArgs);
        lastArgs = undefined;
      }
    }
  };

  return debounced;
}
